import type { ModuleID, VersionString, ViewID } from "@bestowinc/tinkertoy-core";

import type { ActionScope } from "./types";
import { PERFORMANCE_METRIC_NAMES } from "./types";
import { startPerformanceTimer } from "./timers";

function resolveActionErrorCode(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = Reflect.get(error, "code");
    if (typeof code === "string") {
      return code;
    }
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return "abort";
  }
  if (error instanceof Error) {
    return error.name;
  }
  return "unknown";
}

export async function trackActionLatency<T>(
  args: {
    actionScope: ActionScope;
    actionName: string;
    viewId: ViewID;
    viewVersion: VersionString;
    moduleId?: ModuleID;
    moduleVersion?: VersionString;
  },
  run: () => Promise<T>,
): Promise<T> {
  const timer = startPerformanceTimer(PERFORMANCE_METRIC_NAMES.actionLatency, {
    actionScope: args.actionScope,
    actionName: args.actionName,
    viewId: args.viewId,
    viewVersion: args.viewVersion,
    moduleId: args.moduleId,
    moduleVersion: args.moduleVersion,
  });

  try {
    const result = await run();
    timer.complete("success");
    return result;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      timer.complete("superseded", { errorCode: "abort" });
    } else {
      timer.complete("failure", { errorCode: resolveActionErrorCode(error) });
    }
    throw error;
  }
}
